import React, { memo, useEffect, useLayoutEffect, useRef } from 'react';
import {
  formatVariableRef,
  normalizeArrayValues,
  normalizeVariableName,
} from '../../../../utils/variableScope';
import NestedVarField from '../../NestedVarField';
import { TEXT_VALUE_SLASH_IDS } from '../../valueSlash';
import PathSlashUndo from './PathSlashUndo';

const heightsEqual = (a, b) => {
  if (a === b) return true;
  if (!a || !b || a.length !== b.length) return false;
  return a.every((h, i) => h === b[i]);
};

/**
 * Array value fields only — the `[n]}}` token closers live in the name column
 * so the row reads `{{name[1]}}` across Variable → Value.
 *
 * Reports each slot's height via `onSlotHeights` so the name-column closers
 * stay locked to the matching value row as fields grow.
 */
const ArrayValuesCell = memo(({
  name = '',
  values,
  readOnly,
  className = '',
  autoFocusFirst = false,
  onFocusFirst = null,
  onCommitValues,
  onSlotHeights = null,
  resolveVariable = null,
  pickerVariables = [],
  onCreateVariable = null,
  historyEpoch = 0,
  pathSlashOffers = [],
  onUndoPathSlash = null,
}) => {
  const slots = normalizeArrayValues(values);
  const baseName = normalizeVariableName(name) || 'name';
  const slotElsRef = useRef([]);
  const lastHeightsRef = useRef([]);
  const onSlotHeightsRef = useRef(onSlotHeights);
  onSlotHeightsRef.current = onSlotHeights;

  const publishHeights = () => {
    const next = slotElsRef.current
      .slice(0, slots.length)
      .map((el) => Math.max(36, Math.round(el?.getBoundingClientRect().height || 36)));
    if (heightsEqual(lastHeightsRef.current, next)) return;
    lastHeightsRef.current = next;
    onSlotHeightsRef.current?.(next);
  };

  useLayoutEffect(() => {
    publishHeights();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- remeasure when slot count / content chrome changes
  }, [slots.length, historyEpoch, values, readOnly]);

  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => {
      publishHeights();
    });
    slotElsRef.current.slice(0, slots.length).forEach((el) => {
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slots.length, historyEpoch, readOnly]);

  useEffect(
    () => () => {
      lastHeightsRef.current = [];
      onSlotHeightsRef.current?.([]);
    },
    []
  );

  const commitAt = (index, nextValue, opts) => {
    const next = slots.map((v, i) => (i === index ? nextValue : v));
    onCommitValues?.(next, opts);
  };

  const addSlot = () => {
    if (readOnly) return;
    onCommitValues?.([...slots, ''], { silent: false });
  };

  const removeSlot = (index) => {
    if (readOnly || slots.length <= 1) return;
    onCommitValues?.(
      slots.filter((_, i) => i !== index),
      { silent: false }
    );
  };

  return (
    <div className={`variables-panel__array-values ${className}`.trim()}>
      {slots.map((slot, i) => {
        const index = i + 1;
        const ref = formatVariableRef(baseName, index);
        return (
          <div
            key={i}
            className="variables-panel__array-slot"
            ref={(el) => {
              slotElsRef.current[i] = el;
            }}
          >
            <div className="variables-panel__value-field">
              <NestedVarField
                className="variables-panel__input--value"
                value={slot}
                placeholder="value"
                readOnly={readOnly}
                autoFocus={autoFocusFirst && i === 0}
                onFocus={() => {
                  if (i === 0) onFocusFirst?.();
                }}
                onCommit={(value, opts) => commitAt(i, value, opts)}
                ariaLabel={`{{${ref}}}`}
                resolveVariable={resolveVariable}
                pickerVariables={pickerVariables}
                onCreateVariable={readOnly ? null : onCreateVariable}
                historyEpoch={historyEpoch}
                slashAllowed={TEXT_VALUE_SLASH_IDS}
              />
              {pathSlashOffers
                .filter((offer) => offer.slot === i)
                .map((offer) => (
                  <PathSlashUndo key={offer.id} onUndo={() => onUndoPathSlash?.(offer)} />
                ))}
            </div>
            {!readOnly && (
              <button
                type="button"
                className={`variables-panel__array-remove${
                  slots.length <= 1 ? ' is-disabled' : ''
                }`}
                onClick={() => removeSlot(i)}
                disabled={slots.length <= 1}
                title={
                  slots.length <= 1
                    ? 'Arrays need at least one value'
                    : `Remove {{${ref}}}`
                }
                aria-label={
                  slots.length <= 1
                    ? 'Remove disabled — arrays need at least one value'
                    : `Remove {{${ref}}}`
                }
              >
                ×
              </button>
            )}
          </div>
        );
      })}
      {!readOnly && (
        <button
          type="button"
          className="variables-panel__array-add"
          onClick={addSlot}
          title={`Add {{${formatVariableRef(baseName, slots.length + 1)}}}`}
        >
          + Add value
        </button>
      )}
    </div>
  );
});

export default ArrayValuesCell;
